/* Lightweight SVG charts — zero deps. Sufficient for admin dashboard summary. */

export function LineTrend({ data = [], xKey = "date", yKey = "leads", height = 220, color = "#d4af37" }) {
  const w = 720;
  const h = height;
  const pad = { top: 16, right: 12, bottom: 28, left: 32 };
  if (!data.length) return <EmptyState />;

  const values = data.map((d) => Number(d[yKey] || 0));
  const maxY = Math.max(1, ...values);
  const innerW = w - pad.left - pad.right;
  const innerH = h - pad.top - pad.bottom;
  const stepX = data.length > 1 ? innerW / (data.length - 1) : innerW;

  const points = data.map((d, i) => {
    const x = pad.left + i * stepX;
    const y = pad.top + innerH - (Number(d[yKey] || 0) / maxY) * innerH;
    return [x, y];
  });
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${path} L${points[points.length - 1][0]},${pad.top + innerH} L${points[0][0]},${pad.top + innerH} Z`;

  const gridSteps = 4;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" role="img" aria-label="trend">
      {Array.from({ length: gridSteps + 1 }).map((_, i) => {
        const y = pad.top + (innerH * i) / gridSteps;
        const label = Math.round(maxY - (maxY * i) / gridSteps);
        return (
          <g key={i}>
            <line x1={pad.left} x2={w - pad.right} y1={y} y2={y} stroke="#e2e8f0" strokeDasharray="3 3" />
            <text x={pad.left - 6} y={y + 3} fontSize="10" textAnchor="end" fill="#94a3b8">{label}</text>
          </g>
        );
      })}
      <path d={area} fill={color} opacity="0.14" />
      <path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="3" fill={color}>
          <title>{`${data[i][xKey]}: ${data[i][yKey]}`}</title>
        </circle>
      ))}
      {data.map((d, i) => {
        if (i % Math.ceil(data.length / 7) !== 0 && i !== data.length - 1) return null;
        const x = pad.left + i * stepX;
        return <text key={`x${i}`} x={x} y={h - 8} fontSize="10" textAnchor="middle" fill="#94a3b8">{d[xKey]}</text>;
      })}
    </svg>
  );
}

export function HBarChart({ data = [], labelKey = "ref", valueKey = "count", color = "#0f172a" }) {
  if (!data.length) return <EmptyState />;
  const max = Math.max(1, ...data.map((d) => Number(d[valueKey] || 0)));
  return (
    <div className="space-y-2">
      {data.slice(0, 6).map((d, i) => {
        const pct = (Number(d[valueKey] || 0) / max) * 100;
        return (
          <div key={i} className="text-xs">
            <div className="flex justify-between text-slate-600 mb-1">
              <span className="truncate max-w-[220px]" title={d[labelKey]}>{d[labelKey]}</span>
              <span className="font-medium text-slate-800">{d[valueKey]}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmptyState() {
  return <div className="text-slate-400 text-sm h-full grid place-items-center">No data yet.</div>;
}
